import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import {
  Box,
  Button,
  Typography,
  useTheme,
  Grid,
  Chip,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Container,
} from "@mui/material";
import {
  Search,
  LayoutGrid,
  List,
  Plus
} from 'lucide-react';
import { tokens } from "../../theme";
import Modal from "../../utils/components/Modal";
import Cancellation from "../../utils/components/Cancellation";
import EventForm from "./EventForm";
import AdminEventCard from "./AdminEventCard";
import { getAllEvents, deleteEvent, updateEvent, getMediaByEventId, publishEvent } from "./EventServices";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import CustomDataTable from "../../utils/components/CustomDataTable";

const Events = () => {
  const [event, setEvent] = useState(null);
  const setIsLoading = useLoadingSpinner();
  const [updateEventsData, setUpdateEventsData] = useState(false);
  const [params, setParams] = useState({ pageSize: 12, page: 0 });
  const [eventsData, setEventsData] = useState({ content: [], totalElements: 0 });
  const [mediaFiles, setMediaFiles] = useState([]); // State for passed media files
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [showCreationModal, setShowCreationModal] = useState(false);
  const [showEditionModal, setShowEditionModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);

  // High-Volume Optimization States
  const [viewMode, setViewMode] = useState('card');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const lunchEditionModal = async (e) => {
    setEvent(e);
    setIsLoading(true);
    // Fetch media files before opening the modal
    await getMediaByEventId(
      (data) => setMediaFiles(data),
      e.id,
      () => { }, // setApiError placeholder
      () => { }  // setIsLoading placeholder - we handle loading here
    ).catch(err => {
      console.error("Failed to fetch media", err);
      setMediaFiles([]);
    });

    setIsLoading(false);
    setShowEditionModal(true);
  };

  const lunchDeletionModal = (e) => {
    setEvent(e);
    setShowDeletionModal(true);
  };

  const lunchPublishModal = (e) => {
    setEvent(e);
    setShowPublishModal(true);
  };

  const handlePublish = async () => {
    setIsLoading(true);
    await publishEvent(event.id, setUpdateEventsData, () => { }, setIsLoading);
    setShowPublishModal(false);
  };

  const deleteEventHandler = async () => {
    setIsLoading(true);
    await deleteEvent(event.id, setUpdateEventsData, () => { }, setIsLoading);
    setShowDeletionModal(false);
  };

  useEffect(() => {
    setIsLoading(true);
    getAllEvents(params, () => { }, setEventsData, setIsLoading);
  }, [updateEventsData, params, setIsLoading]);

  const filteredEvents = eventsData.content.filter(e => {
    const matchesSearch = e.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' ||
      (statusFilter === 'PUBLISHED' && e.active) ||
      (statusFilter === 'DRAFT' && !e.active);
    return matchesSearch && matchesStatus;
  });

  const columns = [
    {
      field: "title",
      headerName: "Title",
      width: 300,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap="12px">
          <Box sx={{ width: 40, height: 40, borderRadius: '8px', overflow: 'hidden', bgcolor: colors.primary[500] }}>
            <img src={params.row.imageUrl || "placeholder.jpg"} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>
          <Typography fontWeight="bold" color={colors.grey[100]}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    { field: "eventDate", headerName: "Date", width: 180 },
    {
      field: "active", headerName: "Status", width: 150, renderCell: (params) => (
        <Chip
          label={params.value ? "Published" : "Draft"}
          size="small"
          sx={{
            bgcolor: params.value ? `${colors.greenAccent[500]}1A` : `${colors.grey[500]}1A`,
            color: params.value ? colors.greenAccent[500] : colors.grey[300],
            fontWeight: '700'
          }}
        />
      )
    },
    // ... Actions column is handled in the Card/Table or can be added if needed for table view specifically
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: "40px", pb: "80px" }}>
      {/* Enhanced Header Section */}
      <Box
        mb="40px"
        sx={{
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          padding: "32px",
          borderRadius: "24px",
          border: `1px solid ${theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[200]}`,
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb="24px">
          <Box>
            <Typography variant="h2" fontWeight="bold" sx={{ color: colors.grey[100], mb: "4px", fontFamily: "Josefin Sans" }}>
              Events Management
            </Typography>
            <Typography variant="h5" color={theme.palette.mode === 'dark' ? colors.greenAccent[500] : colors.greenAccent[300]} fontWeight="500">
              {filteredEvents.length} upcoming experiences scheduled
            </Typography>
          </Box>

          <Box display="flex" gap="12px" alignItems="center">
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, next) => next && setViewMode(next)}
              size="small"
              sx={{
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                borderRadius: '12px',
                p: '4px',
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: '8px',
                  color: colors.grey[300],
                  '&.Mui-selected': {
                    bgcolor: colors.greenAccent[500],
                    color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                    '&:hover': { bgcolor: colors.greenAccent[400] }
                  }
                }
              }}
            >
              <ToggleButton value="card"><LayoutGrid size={18} /></ToggleButton>
              <ToggleButton value="list"><List size={18} /></ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              startIcon={<Plus size={20} />}
              onClick={() => {
                setMediaFiles([]); // Reset media for new event
                setShowCreationModal(true);
              }}
              sx={{
                backgroundColor: colors.greenAccent[500],
                color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                fontWeight: "bold",
                padding: "10px 24px",
                borderRadius: "12px",
                textTransform: "none",
                boxShadow: `0 8px 20px ${colors.greenAccent[500]}33`,
                "&:hover": {
                  backgroundColor: colors.greenAccent[400],
                  transform: "translateY(-2px)"
                },
                transition: "all 0.2s"
              }}
            >
              New Event
            </Button>
          </Box>
        </Box>

        <Box display="flex" gap="16px" alignItems="center">
          <TextField
            placeholder="Search events by title or description..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '16px',
                bgcolor: theme.palette.mode === "dark" ? "rgba(0,0,0,0.2)" : "#FFFFFF",
                '& fieldset': { borderColor: theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[200] }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} color={colors.grey[300]} />
                </InputAdornment>
              ),
            }}
          />
          <Box display="flex" gap="8px">
            {['ALL', 'PUBLISHED', 'DRAFT'].map((status) => (
              <Chip
                key={status}
                label={status}
                onClick={() => setStatusFilter(status)}
                sx={{
                  borderRadius: '10px',
                  fontWeight: '600',
                  bgcolor: statusFilter === status ? colors.blueAccent[500] : 'transparent',
                  color: statusFilter === status ? '#FFFFFF' : colors.grey[300],
                  border: `1px solid ${statusFilter === status ? colors.blueAccent[500] : colors.grey[400]}`,
                  '&:hover': { bgcolor: statusFilter === status ? colors.blueAccent[400] : 'rgba(255,255,255,0.05)' }
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Grid or List View */}
      {viewMode === 'card' ? (
        <Grid container spacing={3}>
          {filteredEvents.map((e) => (
            <Grid item xs={12} md={6} lg={4} key={e.id}>
              <AdminEventCard
                event={e}
                onEdit={lunchEditionModal}
                onPublish={lunchPublishModal}
                onDelete={lunchDeletionModal}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <CustomDataTable
          addRecordButtonTitle={"Add Event"}
          headerColumn={columns}
          lunchCreateModal={() => {
            setMediaFiles([]);
            setShowCreationModal(true);
          }}
          lunchDeletionModal={lunchDeletionModal}
          paginationModel={params}
          rows={{ ...eventsData, content: filteredEvents }}
          setPaginationModel={setParams}
          tableTitle={"Events List"}
        />
      )}

      {/* Modals */}
      <Modal isOpen={showCreationModal} onClose={setShowCreationModal}>
        <EventForm
          isUpdating={false}
          setState={setUpdateEventsData}
          toggleModal={setShowCreationModal}
          mediaFiles={[]}
        />
      </Modal>
      <Modal isOpen={showEditionModal} onClose={setShowEditionModal}>
        <EventForm
          event={event}
          isUpdating={true}
          updateEvent={updateEvent}
          setState={setUpdateEventsData}
          toggleModal={setShowEditionModal}
          mediaFiles={mediaFiles}
        />
      </Modal>
      <Modal isOpen={showDeletionModal} onClose={setShowDeletionModal}>
        <Cancellation
          actionHandler={deleteEventHandler}
          actionText={"Delete"}
          cancelText={"Go Back"}
          setOpen={setShowDeletionModal}
          title={`Delete Event`}
          warningMessage={`Are you sure you want to delete ${event?.title}? This action cannot be undone.`}
        />
      </Modal>
      <Modal isOpen={showPublishModal} onClose={setShowPublishModal}>
        <Cancellation
          actionHandler={handlePublish}
          actionText={event?.active ? "Unpublish" : "Publish"}
          cancelText={"Go Back"}
          setOpen={setShowPublishModal}
          title={event?.active ? "Unpublish Event" : "Publish Event"}
          warningMessage={`Are you sure you want to ${event?.active ? "unpublish" : "publish"} ${event?.title}?`}
        />
      </Modal>
      <ToastContainer />
    </Container>
  );
};

export default Events;
